import React, { useCallback, useEffect, useMemo, useState } from "react";
import { axiosSalary, axiosMaster } from "../../api/axiosInstance";
import { FaFileExcel } from "react-icons/fa";

/* ═══════════════════════════════════════════════════
   DATE UTILITIES
   All dates from your API come in two formats:
     1. ISO with timezone  → "2026-06-17T00:00:00+05:30" or "2026-06-06T00:00:00Z"
     2. Display string     → "17/06/2026" (DD/MM/YYYY)  ← new Date() breaks on this

   Solution: convert everything to a plain integer YYYYMMDD (e.g. 20260617)
   for comparison — no Date objects, no timezone shift, no Invalid Date.
   ═══════════════════════════════════════════════════ */

/**
 * Convert any date string to YYYYMMDD integer for safe comparison.
 * Handles:
 *   "2026-06-17T00:00:00+05:30"  → 20260617
 *   "2026-06-06T00:00:00Z"       → 20260606
 *   "17/06/2026"  (DD/MM/YYYY)   → 20260617
 *   "2026-06-17"  (YYYY-MM-DD)   → 20260617
 * Returns null for falsy input.
 */
const toDateInt = (str) => {
  if (!str) return null;
  const s = String(str);

  // ISO format: starts with YYYY — take only the date part before T
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return parseInt(s.slice(0, 10).replace(/-/g, ""), 10);
  }

  // DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return parseInt(`${yyyy}${mm}${dd}`, 10);
  }

  return null;
};

/* ─────────────────────────────────────────────
   isActiveOnDate
   Returns true if the employee was employed on reportDateStr.

   Business Rules:
   1. joiningDate missing           → hide (no valid tenure)
   2. relievingDate = null          → still working → show if joined <= date
   3. relievingDate has a date      → show only if joined <= date <= relieved
   4. relievingDate < joiningDate   → bad data → treat as active from joiningDate
   5. employmentHistory[] supported → for multi-tenure (rejoin) scenarios
   ───────────────────────────────────────────── */
const isActiveOnDate = (emp, reportDateStr) => {
  const rd = toDateInt(reportDateStr);
  if (!rd) return false;

  const tenures =
    Array.isArray(emp.employmentHistory) && emp.employmentHistory.length > 0
      ? emp.employmentHistory
      : [{ joiningDate: emp.joiningDate, relievingDate: emp.relievingDate ?? null }];

  return tenures.some((tenure) => {
    const join    = toDateInt(tenure.joiningDate);
    const relieve = toDateInt(tenure.relievingDate);

    if (!join) return false;

    // ✅ Business Rule: if relievingDate has ANY value → employee has left → hide
    if (relieve) return false;

    // No relievingDate → currently working → show if joined on or before report date
    return join <= rd;
  });
};

/* ═══════════════════════════════════════════════════
   ATTENDANCE STATUS — single source of truth
   Every place that needs a row's status (summary cards,
   employee-wise summary, table badge, CSV export) calls
   getStatus(row). Change the priority once here and it
   is consistent everywhere.
   ═══════════════════════════════════════════════════ */
const STATUS = {
  SUSPENSE:     "Suspense",
  QUARTER_DAY:  "Quarter Day",
  HALF_DAY:     "Half Day",
  ABSENT:       "Absent",
  LATE:         "Late",
  PRESENT:      "Present",
};

const STATUS_ORDER = [
  STATUS.PRESENT,
  STATUS.ABSENT,
  STATUS.LATE,
  STATUS.SUSPENSE,
  STATUS.HALF_DAY,
  STATUS.QUARTER_DAY,
];

const STATUS_STYLE = {
  [STATUS.PRESENT]:     { icon: "🟢", className: "text-green-600 font-medium" },
  [STATUS.ABSENT]:      { icon: "🔴", className: "text-red-600 font-medium" },
  [STATUS.LATE]:        { icon: "🟡", className: "text-yellow-600 font-medium" },
  [STATUS.SUSPENSE]:    { icon: "🟣", className: "text-purple-600 font-medium" },
  [STATUS.HALF_DAY]:    { icon: "🌓", className: "text-orange-600 font-medium" },
  [STATUS.QUARTER_DAY]: { icon: "◐",  className: "text-pink-600 font-medium" },
};

/**
 * Priority (highest to lowest):
 *   Suspense > Quarter Day > Half Day > Absent > Late > Present
 */
const getStatus = (row) => {
  if (row.isSuspense)   return STATUS.SUSPENSE;
  if (row.isQuarterDay) return STATUS.QUARTER_DAY;
  if (row.isHalfDay)    return STATUS.HALF_DAY;
  if (row.isAbsent)     return STATUS.ABSENT;
  if (row.isLate)       return STATUS.LATE;
  return STATUS.PRESENT;
};

/** Tally an array of rows into { Present: n, Absent: n, ... } */
const tallyStatuses = (rows) => {
  const counts = STATUS_ORDER.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
  rows.forEach((row) => { counts[getStatus(row)] += 1; });
  return counts;
};

/* ─────────────────────────────────────────────
   CSV escape helper
   ───────────────────────────────────────────── */
const escapeCSV = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
  if (safe.includes(",") || safe.includes('"') || safe.includes("\n")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const PeriodicAttendanceReport = () => {

  /* ─── USER ─── */
  let storedUser = {};
  try { storedUser = JSON.parse(localStorage.getItem("user") || "{}"); } catch { }
  const groupId = storedUser?.groupId;

  /* ─── STATE ─── */
  const today = new Date().toISOString().split("T")[0];

  const [fromDate,           setFromDate]           = useState(today);
  const [toDate,             setToDate]             = useState(today);
  const [data,               setData]               = useState([]);
  const [loading,            setLoading]            = useState(false);
  const [restaurants,        setRestaurants]        = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");

  /* ─── FETCH RESTAURANTS (once on mount) ─── */
  const fetchRestaurants = useCallback(async () => {
    if (!groupId) return;
    try {
      const res = await axiosSalary.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);
      setRestaurants(res.data || []);
    } catch (err) {
      console.error("Restaurant fetch error:", err);
    }
  }, [groupId]);

  /* ─── FETCH REPORT + EMPLOYEE MASTER ────────────────────────────────
     Both in one Promise.all — no race condition between emp data and
     attendance data.

     Per-row filter:
       row.attendanceDate  → "2026-06-17T00:00:00+05:30"  (ISO, reliable)
       row.attendanceDateDisplay → "17/06/2026" (DD/MM/YYYY, fallback)

     toDateInt() handles both formats safely without timezone shift.
  ─────────────────────────────────────────────────────────────────── */
  const fetchReport = useCallback(async () => {
    if (!groupId || !selectedRestaurant || !fromDate || !toDate) {
      setData([]);
      return;
    }
    try {
      setLoading(true);

      const [attRes, empRes] = await Promise.all([
        axiosSalary.get(
          `/DailyAttendanceReport/DailyReportByRestaurant?groupId=${groupId}&restId=${selectedRestaurant}&fromDate=${fromDate}&toDate=${toDate}`
        ),
        axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`),
      ]);

      // Build empId → employee map for O(1) lookup
      const empMap = {};
      (empRes.data || []).forEach((e) => { empMap[e.empId] = e; });

      // Filter each row using safe integer date comparison
      const filtered = (attRes.data || []).filter((row) => {
        const emp = empMap[row.empId];
        if (!emp) return false;

        // Prefer attendanceDate (ISO) over attendanceDateDisplay (DD/MM/YYYY)
        const rowDateStr = row.attendanceDate || row.attendanceDateDisplay;
        return isActiveOnDate(emp, rowDateStr);
      });

      setData(filtered);

    } catch (err) {
      console.error("Attendance Report Error:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [groupId, selectedRestaurant, fromDate, toDate]);

  /* ─── EFFECTS ─── */
  useEffect(() => { fetchRestaurants(); }, [fetchRestaurants]);

  useEffect(() => {
    if (fromDate && toDate && selectedRestaurant) fetchReport();
    else setData([]);
  }, [fetchReport, fromDate, toDate, selectedRestaurant]);

  /* ─── TOTAL DAYS ─── */
  const totalDaysRange = useMemo(() => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end   = new Date(toDate);
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [fromDate, toDate]);

  /* ─── GROUP BY EMPLOYEE ─── */
  const groupedEmployees = useMemo(() => {
    return Object.values(
      data.reduce((acc, row) => {
        if (!acc[row.empId]) acc[row.empId] = [];
        acc[row.empId].push(row);
        return acc;
      }, {})
    );
  }, [data]);

  /* ─── OVERALL SUMMARY (top cards) ───────────────────────────────
     Hoisted to top level — never call hooks inside .map()/loops.
  ─────────────────────────────────────────────────────────────── */
  const overallSummary = useMemo(() => ({
    totalEmployees: groupedEmployees.length,
    ...tallyStatuses(data),
  }), [data, groupedEmployees]);

  /* ─── HELPERS ─── */
  const minutesToHours = (minutes) => {
    const hrs  = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  /* ─── EXPORT ─── */
  const exportExcel = () => {
    if (!groupedEmployees.length) return;

    const restaurantName = selectedRestaurant
      ? restaurants.find((r) => Number(r.restId) === Number(selectedRestaurant))
          ?.restaurantName || "Restaurant"
      : "All_Restaurants";

    let rows = [];

    groupedEmployees.forEach((empRows) => {
      const emp = empRows[0];
      const counts = tallyStatuses(empRows);

      const summaryRow = {
        EmployeeName:  emp.empName,
        EmployeeId:    emp.empId,
        FromDate:      fromDate,
        ToDate:        toDate,
        Days:          totalDaysRange,
        Present:       counts[STATUS.PRESENT],
        Absent:        counts[STATUS.ABSENT],
        Late:          counts[STATUS.LATE],
        Suspense:      counts[STATUS.SUSPENSE],
        HalfDay:       counts[STATUS.HALF_DAY],
        QuarterDay:    counts[STATUS.QUARTER_DAY],
        WorkingHours:  minutesToHours(empRows.reduce((a, b) => a + (b.workingMinutes  || 0), 0)),
        OvertimeHours: minutesToHours(empRows.reduce((a, b) => a + (b.overtimeMinutes || 0), 0)),
      };

      rows.push(["EMPLOYEE SUMMARY"]);
      rows.push(Object.keys(summaryRow));
      rows.push(Object.values(summaryRow));
      rows.push([]);
      rows.push(["EmpId", "Name", "Date", "First Punch", "Last Punch", "Working Hours", "OT Hours", "Status"]);

      empRows.forEach((r) => {
        rows.push([
          r.empId,
          r.empName,
          r.attendanceDateDisplay,
          r.firstPunchDisplay ? `\t${r.firstPunchDisplay}` : "",
          r.lastPunchDisplay  ? `\t${r.lastPunchDisplay}`  : "",
          minutesToHours(r.workingMinutes  || 0),
          minutesToHours(r.overtimeMinutes || 0),
          getStatus(r),
        ]);
      });

      rows.push([]);
      rows.push([]);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((row) => row.map(escapeCSV).join(",")).join("\n");

    const link = document.createElement("a");
    link.href     = encodeURI(csvContent);
    link.download = `${restaurantName}_Attendance_Report_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ═══════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════ */
  return (
    <div className="bg-white p-5 rounded-2xl shadow border">

      {/* ── HEADER ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-xl font-semibold text-gray-800">
          Periodic Attendance Report
        </h2>

        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          />
          <select
            value={selectedRestaurant}
            onChange={(e) => setSelectedRestaurant(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          >
            <option value="">-- Select Restaurant --</option>
            {restaurants.map((rest) => (
              <option key={rest.restId} value={rest.restId}>
                {rest.restaurantName}
              </option>
            ))}
          </select>
          <button
            onClick={exportExcel}
            disabled={!groupedEmployees.length}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel />
            Export
          </button>
        </div>
      </div>

      {/* ── TOP SUMMARY CARDS ── */}
      {!!selectedRestaurant && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
          <SummaryCard label="Total Employees" value={overallSummary.totalEmployees} className="bg-gray-100 text-gray-800" />
          <SummaryCard label="Present"     value={overallSummary[STATUS.PRESENT]}     className="bg-green-50 text-green-700" />
          <SummaryCard label="Absent"      value={overallSummary[STATUS.ABSENT]}      className="bg-red-50 text-red-700" />
          <SummaryCard label="Late"        value={overallSummary[STATUS.LATE]}        className="bg-yellow-50 text-yellow-700" />
          <SummaryCard label="Suspense"    value={overallSummary[STATUS.SUSPENSE]}    className="bg-purple-50 text-purple-700" />
          <SummaryCard label="Half Day"    value={overallSummary[STATUS.HALF_DAY]}    className="bg-orange-50 text-orange-700" />
          <SummaryCard label="Quarter Day" value={overallSummary[STATUS.QUARTER_DAY]} className="bg-pink-50 text-pink-700" />
        </div>
      )}

      {/* ── TABLE ── */}
      <div className="border rounded-xl overflow-auto max-h-[600px]">
        <table className="w-full text-sm">

          <thead className="bg-gray-200 sticky top-0 z-10">
            <tr>
              <th className="p-2 border">ID</th>
              <th className="p-2 border text-left">Name</th>
              <th className="p-2 border">Date</th>
              <th className="p-2 border">First Punch</th>
              <th className="p-2 border">Last Punch</th>
              <th className="p-2 border">Working Hours</th>
              <th className="p-2 border">OT Hours</th>
              <th className="p-2 border">Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center p-5 text-gray-500">Loading...</td>
              </tr>
            ) : !selectedRestaurant ? (
              <tr>
                <td colSpan="8" className="text-center p-5 text-gray-400">
                  Please select a restaurant to view the report.
                </td>
              </tr>
            ) : groupedEmployees.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center p-5 text-gray-400">
                  No data found for the selected range.
                </td>
              </tr>
            ) : (
              groupedEmployees.map((empRows, index) => {
                const emp = empRows[0];
                const counts = tallyStatuses(empRows);

                const workingMinutes  = empRows.reduce((a, b) => a + (b.workingMinutes  || 0), 0);
                const overtimeMinutes = empRows.reduce((a, b) => a + (b.overtimeMinutes || 0), 0);

                return (
                  <React.Fragment key={index}>

                    {/* EMPLOYEE SUMMARY ROW */}
                    <tr className="bg-blue-100">
                      <td colSpan="8" className="p-3 font-semibold">
                        <div className="flex flex-wrap gap-5">
                          <span>👤 {emp.empName}</span>
                          <span>📅 Days: {empRows.length}</span>
                          <span className="text-green-700">✅ Present: {counts[STATUS.PRESENT]}</span>
                          <span className="text-red-700">❌ Absent: {counts[STATUS.ABSENT]}</span>
                          <span className="text-yellow-700">⏰ Late: {counts[STATUS.LATE]}</span>
                          <span className="text-purple-700">🟣 Suspense: {counts[STATUS.SUSPENSE]}</span>
                          <span className="text-orange-700">🌓 Half Day: {counts[STATUS.HALF_DAY]}</span>
                          <span className="text-pink-700">◐ Quarter Day: {counts[STATUS.QUARTER_DAY]}</span>
                          <span>🕒 Work: {minutesToHours(workingMinutes)}</span>
                          <span>🚀 OT: {minutesToHours(overtimeMinutes)}</span>
                        </div>
                      </td>
                    </tr>

                    {/* DAILY DETAIL ROWS */}
                    {empRows.map((row, i) => {
                      const status = getStatus(row);
                      const style  = STATUS_STYLE[status];
                      return (
                        <tr key={i} className="hover:bg-blue-50">
                          <td className="p-2 border text-center">{row.empId}</td>
                          <td className="p-2 border">{row.empName}</td>
                          <td className="p-2 border text-center">{row.attendanceDateDisplay}</td>
                          <td className="p-2 border text-center">{row.firstPunchDisplay || "—"}</td>
                          <td className="p-2 border text-center">{row.lastPunchDisplay  || "—"}</td>
                          <td className="p-2 border text-center">{minutesToHours(row.workingMinutes  || 0)}</td>
                          <td className="p-2 border text-center">{minutesToHours(row.overtimeMinutes || 0)}</td>
                          <td className="p-2 border text-center">
                            <span className={style.className}>{style.icon} {status}</span>
                          </td>
                        </tr>
                      );
                    })}

                  </React.Fragment>
                );
              })
            )}
          </tbody>

        </table>
      </div>

    </div>
  );
};

/* ─────────────────────────────────────────────
   Small presentational card for the top summary strip
   ───────────────────────────────────────────── */
const SummaryCard = ({ label, value, className }) => (
  <div className={`rounded-xl p-3 text-center ${className}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs font-medium uppercase tracking-wide">{label}</div>
  </div>
);

export default PeriodicAttendanceReport;